// src/config/blindBiddingConfig.js
// Configuración y helpers para el sistema de ofertas ciegas

/**
 * DURACIÓN POR DEFECTO DEL PERIODO DE OFERTA CIEGA
 * Cuando se registra la primera oferta, arranca este contador
 */
export const BLIND_BIDDING_DURATION = 72; // horas

/**
 * UMBRALES POR DEFECTO PARA EL SEMÁFORO
 * green: ofertas dentro de este % del precio de salida
 * yellow: ofertas entre green y yellow % por debajo
 * red: ofertas más de yellow % por debajo
 */
export const DEFAULT_SEMAPHORE_THRESHOLDS = {
  green: 3,   // <= 3% por debajo = verde
  yellow: 9   // <= 9% por debajo = amarillo, > 9% = rojo
};

/**
 * Calcula la fecha de fin del periodo de oferta ciega
 * Prioriza blind_bidding_deadline si existe, sino calcula desde started_at + duration
 * @param {Object} property - Objeto de propiedad
 * @param {string} [property.blind_bidding_deadline] - Deadline explícito (ISO string, preferido)
 * @param {string} [property.blind_bidding_started_at] - Inicio del periodo (ISO string)
 * @param {number} [property.blind_bidding_duration=72] - Duración en horas (fallback)
 * @returns {Date|null} Fecha de fin calculada o null si no hay periodo activo
 */
export const getBlindBiddingEndDate = (property) => {
  // Prioridad 1: usar deadline si existe (nuevo formato)
  if (property?.blind_bidding_deadline) {
    return new Date(property.blind_bidding_deadline);
  }

  // Fallback: calcular con duration (legacy)
  if (!property?.blind_bidding_started_at) {
    return null;
  }

  const startDate = new Date(property.blind_bidding_started_at);
  const duration = property.blind_bidding_duration || BLIND_BIDDING_DURATION;

  return new Date(startDate.getTime() + duration * 60 * 60 * 1000);
};

/**
 * Verifica si una propiedad está actualmente en periodo de oferta ciega activo
 * Chequea que haya iniciado (blind_bidding_started_at) y no haya expirado
 * @param {Object} property - Objeto de propiedad
 * @returns {boolean} true si el periodo está activo (no expirado)
 */
export const isInBlindBidding = (property) => {
  if (!property?.blind_bidding_started_at) {
    return false;
  }

  const now = new Date();
  const endDate = getBlindBiddingEndDate(property);
  
  return endDate && now < endDate;
};

/**
 * Indica si el usuario tiene el flag de acceso anticipado a fase ciega.
 * Nota: el acceso desde UPCOMING se controla via isBlindPhase en PropertyContent
 * (que incluye isUpcoming && invite.canBlindBid). Esta función solo cubre
 * BLIND_WAITING y BLIND_ACTIVE para el prop informativo de BlindBidCard.
 * @param {Object} invite - Objeto de invitación del comprador
 * @param {string} propertyStatus - Estado actual de la propiedad
 * @returns {boolean}
 */
export const canUserBlindBid = (invite, propertyStatus) => {
  if (!invite?.canBlindBid) {
    return false;
  }

  return propertyStatus === 'BLIND_WAITING' || propertyStatus === 'BLIND_ACTIVE';
};

/**
 * Obtiene los umbrales de semáforo configurados para una propiedad
 * Usa umbrales personalizados si existen, sino aplica defaults (green: 3%, yellow: 9%)
 * @param {Object} property - Objeto de propiedad
 * @param {number} [property.semaphore_green_threshold] - Umbral verde personalizado
 * @param {number} [property.semaphore_yellow_threshold] - Umbral amarillo personalizado
 * @returns {Object} Umbrales { green: number, yellow: number }
 */
export const getSemaphoreThresholds = (property) => {
  return {
    green: property?.semaphore_green_threshold || DEFAULT_SEMAPHORE_THRESHOLDS.green,
    yellow: property?.semaphore_yellow_threshold || DEFAULT_SEMAPHORE_THRESHOLDS.yellow
  };
};